import { useCallback, useEffect, useRef, useState } from 'react';
import type { AutoJoinPrefs, CalendarEventDTO } from '../../global';
import { isBotJoinable, wantsAutoJoin } from '../../../../src/shared/autoJoin';

/** Botu henüz toplantıya girmemiş durumlar — düğme kapatılınca bot iptal edilebilir. */
const PRE_JOIN_STATUSES = new Set(['creating', 'scheduled', 'booting', 'joining']);

const EMPTY_PREFS: AutoJoinPrefs = { autoJoinBot: false, autoJoinExcluded: [], autoJoinIncluded: [] };

export type EventBotState =
  /** Bot gönderilemez (link yok ya da tüm gün). */
  | { kind: 'none' }
  /** Aç/kapa düğmesi: açıksa bot gidecek (ya da planlandı, henüz girmedi). */
  | { kind: 'toggle'; on: boolean }
  /** Bot toplantıda, bitti ya da başarısız — düğme yerine durum gösterilir. */
  | { kind: 'status'; status: string };

/**
 * Yaklaşan toplantılar kartının bot düğmeleri: genel ayar (başlıktaki ana
 * düğme) ve toplantı başına seçimler. Ana düğme toplantılarda elle yapılan
 * seçimlere dokunmuyor; toplantının düğmesi de ana düğmeyi değiştirmiyor
 * (kural: src/shared/autoJoin.ts).
 */
export function useAutoJoin() {
  const [prefs, setPrefs] = useState<AutoJoinPrefs>(EMPTY_PREFS);
  const [scheduled, setScheduled] = useState<Record<string, { meetingId: string; status: string }>>({});
  const [loaded, setLoaded] = useState(false);
  const mountedRef = useRef(true);

  const load = useCallback(async () => {
    const [nextPrefs, nextScheduled] = await Promise.all([
      window.zabit.getAutoJoinPrefs().catch(() => null),
      window.zabit.getBotScheduleMap().catch(() => null),
    ]);
    if (!mountedRef.current) return;
    if (nextPrefs) setPrefs(nextPrefs);
    if (nextScheduled) setScheduled(nextScheduled);
    setLoaded(true);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void load();
    // Bot planlanınca / durumu değişince ana süreç (web'de köprü) haber veriyor.
    const unsubscribe = window.zabit.onMeetingsChanged(() => void load());
    return () => {
      mountedRef.current = false;
      unsubscribe();
    };
  }, [load]);

  /** Ana düğme — yalnızca genel ayar değişir. */
  const setEnabled = useCallback(async (enabled: boolean) => {
    setPrefs((prev) => ({ ...prev, autoJoinBot: enabled }));
    try {
      const next = await window.zabit.setAutoJoinEnabled(enabled);
      if (mountedRef.current) setPrefs(next);
    } catch (err) {
      void load();
      throw err;
    }
  }, [load]);

  /** Toplantının düğmesi — yalnızca o toplantının seçimi değişir. */
  const setEventJoin = useCallback(async (event: CalendarEventDTO, join: boolean) => {
    setPrefs((prev) => ({
      ...prev,
      autoJoinExcluded: join ? prev.autoJoinExcluded.filter((id) => id !== event.id) : [...prev.autoJoinExcluded, event.id],
      autoJoinIncluded: join ? [...prev.autoJoinIncluded, event.id] : prev.autoJoinIncluded.filter((id) => id !== event.id),
    }));
    try {
      const next = await window.zabit.setAutoJoinEvent(event.id, join);
      if (mountedRef.current) setPrefs(next);
    } finally {
      // Planlanan / iptal edilen bot kaydı da tazelensin.
      void load();
    }
  }, [load]);

  const stateFor = useCallback(
    (event: CalendarEventDTO): EventBotState => {
      if (!isBotJoinable(event)) return { kind: 'none' };
      const bot = scheduled[event.id];
      if (bot && !PRE_JOIN_STATUSES.has(bot.status)) return { kind: 'status', status: bot.status };
      // Planlanmış bot düğmeyi açık gösterir; kapatılırsa iptal edilir.
      return { kind: 'toggle', on: Boolean(bot) || wantsAutoJoin(event, prefs) };
    },
    [prefs, scheduled]
  );

  return { prefs, loaded, enabled: prefs.autoJoinBot, setEnabled, setEventJoin, stateFor };
}
